/**
 * Iter 67en+++ — Search Console diagnostic tile.
 *
 * Single-glance health card for the admin: tells you in one screen
 * whether RentalCar.al is still discoverable by Google + Bing.
 *
 * Four pass/fail checks: sitemap reachable, robots.txt reachable,
 * ≥1 google-site-verification TXT record present on the apex domain,
 * and ≥1 IndexNow ping in the last 7 days. Plus secondary info:
 * sitemap URL count, last-pinged URL+timestamp, total TXT records,
 * deep-links to Google Search Console and Bing Webmaster Tools.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ExternalLink, CheckCircle2, AlertCircle, RefreshCw, Loader2, Globe2 } from "lucide-react";

function _fmtAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffSec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function Pill({ ok, label, testId }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs ${
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"
      }`}
      data-testid={testId}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
      <span className="font-semibold">{label}</span>
    </div>
  );
}

export default function AdminSearchConsoleHealthTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/seo/search-console-health");
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data && loading) {
    return <div className="rounded-2xl border border-border bg-white p-5 text-sm text-neutral-500" data-testid="admin-gsc-loading">Loading Search Console health…</div>;
  }
  if (!data) return null;

  const overall = data.overall;
  // Tailwind JIT cannot detect dynamic class concatenation, so we map each
  // overall-status to an explicit static class set that the scanner sees.
  const overallStyles = {
    healthy:  { iconBg: "bg-emerald-100 text-emerald-700", chip: "bg-emerald-100 text-emerald-800" },
    degraded: { iconBg: "bg-amber-100 text-amber-700",     chip: "bg-amber-100 text-amber-800" },
    broken:   { iconBg: "bg-rose-100 text-rose-700",       chip: "bg-rose-100 text-rose-800" },
  }[overall] || { iconBg: "bg-neutral-100 text-neutral-700", chip: "bg-neutral-100 text-neutral-800" };

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="admin-gsc-health">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-xl ${overallStyles.iconBg} flex items-center justify-center`}>
            <Search className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-base font-semibold flex items-center gap-2">
              Search Console status
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${overallStyles.chip}`}>
                {overall}
              </span>
            </div>
            <div className="text-xs text-neutral-500 inline-flex items-center gap-1.5 mt-0.5">
              <Globe2 className="h-3 w-3" /> {data.apex_domain} · checked {_fmtAgo(data.checked_at)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-semibold transition disabled:opacity-50"
          data-testid="admin-gsc-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Re-check
        </button>
      </div>

      {/* 4-check pill grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="admin-gsc-checks">
        <Pill ok={data.health_checks.sitemap_reachable} label="Sitemap reachable" testId="gsc-check-sitemap" />
        <Pill ok={data.health_checks.robots_reachable} label="robots.txt reachable" testId="gsc-check-robots" />
        <Pill ok={data.health_checks.gsc_verification_present} label="GSC TXT verified" testId="gsc-check-dns" />
        <Pill ok={data.health_checks.indexnow_active_7d} label="IndexNow active (7d)" testId="gsc-check-indexnow" />
      </div>

      {/* Secondary info — 2-column layout. Compact, hover for full detail. */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-neutral-700 mb-4">
        <div className="flex justify-between gap-2 border-b border-neutral-100 py-1">
          <dt className="text-neutral-500">Sitemap URLs</dt>
          <dd className="font-mono tabular-nums">{data.sitemap.url_count ?? "—"}</dd>
        </div>
        <div className="flex justify-between gap-2 border-b border-neutral-100 py-1">
          <dt className="text-neutral-500">DNS TXT records on apex</dt>
          <dd className="font-mono tabular-nums">{data.dns_txt.total_txt_records ?? "—"}</dd>
        </div>
        <div className="flex justify-between gap-2 border-b border-neutral-100 py-1">
          <dt className="text-neutral-500">IndexNow pings (24h / 7d)</dt>
          <dd className="font-mono tabular-nums">{data.indexnow.pings_24h} / {data.indexnow.pings_7d}</dd>
        </div>
        <div className="flex justify-between gap-2 border-b border-neutral-100 py-1">
          <dt className="text-neutral-500">Last ping</dt>
          <dd className="truncate" title={data.indexnow.last_ping?.url || ""}>
            {data.indexnow.last_ping ? _fmtAgo(data.indexnow.last_ping.last_pinged_at) : "—"}
          </dd>
        </div>
        <div className="flex justify-between gap-2 border-b border-neutral-100 py-1 sm:col-span-2">
          <dt className="text-neutral-500">Last Bing-confirmed indexation</dt>
          <dd className="truncate" title={data.last_indexed?.url || ""}>
            {data.last_indexed
              ? `${_fmtAgo(data.last_indexed.indexed_at_first_seen)} · ${data.last_indexed.url}`
              : "no record yet — configure Bing Webmaster API key for live verdicts"}
          </dd>
        </div>
      </dl>

      {/* If DNS verification AND meta-tag verification are both missing,
          surface the fix path inline. Either one is enough to satisfy
          Google ownership — admin chooses the easier route. */}
      {!data.health_checks.gsc_verification_present && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-900 mb-3" data-testid="admin-gsc-fix-dns">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" /> Google Search Console ownership not detected
          </div>
          Two paths to verify — pick whichever is easier:
          <ul className="list-disc ml-5 mt-1.5 space-y-1">
            <li>
              <strong>Meta tag</strong> (no DNS access needed): Open{" "}
              <a href={data.search_console_url} target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                Google Search Console
              </a>{" "}
              → add property → "HTML tag" method → copy the <code className="font-mono bg-white/60 px-1 rounded">content="..."</code> value
              and paste it into <strong>Admin → Site Settings → Google Search Console token</strong>.
            </li>
            <li>
              <strong>DNS TXT</strong> (preferred for apex-level ownership): GSC → add property → "DNS" method, then add the
              provided <code className="font-mono bg-white/60 px-1 rounded">google-site-verification=</code> TXT record to{" "}
              <code className="font-mono bg-white/60 px-1 rounded">{data.apex_domain}</code>.
            </li>
          </ul>
        </div>
      )}

      {/* When verified, show which method(s) are active. */}
      {data.meta_verification?.set && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-mono text-emerald-900 mb-3 break-all" data-testid="admin-gsc-meta-tag">
          <div className="text-[10px] uppercase tracking-wider font-bold mb-1 font-sans">Meta-tag verification active</div>
          · {data.meta_verification.value_preview}
        </div>
      )}

      {data.dns_txt.tokens?.length > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-mono text-emerald-900 mb-3 break-all" data-testid="admin-gsc-tokens">
          <div className="text-[10px] uppercase tracking-wider font-bold mb-1 font-sans">Verified GSC tokens</div>
          {data.dns_txt.tokens.map((t, i) => <div key={i}>· {t}</div>)}
        </div>
      )}

      {/* Deep-link CTAs */}
      <div className="flex flex-wrap gap-2">
        <a
          href={data.search_console_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold transition"
          data-testid="admin-gsc-open-google"
        >
          Open Google Search Console <ExternalLink className="h-3 w-3" />
        </a>
        <a
          href={data.bing_webmaster_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-semibold transition"
          data-testid="admin-gsc-open-bing"
        >
          Open Bing Webmaster <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
